<script lang="ts" setup>
import { ref, computed, onMounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useBountySubmissionStore } from '../../stores/bounty-submission'
import DateTimePicker from '../forms/DateTimePicker.vue'

const MAX_BOUNTY_WINDOW_DAYS = 7

const bountyStore = useBountySubmissionStore()
const { form } = storeToRefs(bountyStore)

const startDate = ref<Date>(new Date())
const endDate = ref<Date>(new Date())

const errors = ref<string[]>([])

onMounted(() => {
  if (form.value.windowStartDateTime) startDate.value = form.value.windowStartDateTime
  if (form.value.windowEndDateTime) endDate.value = form.value.windowEndDateTime
})

watch(startDate, (val) => {
  form.value.windowStartDateTime = val
})
watch(endDate, (val) => {
  form.value.windowEndDateTime = val
})

const durationMs = computed(() => {
  if (!form.value.windowStartDateTime || !form.value.windowEndDateTime) return null
  return form.value.windowEndDateTime.getTime() - form.value.windowStartDateTime.getTime()
})

const durationLabel = computed(() => {
  if (durationMs.value === null || durationMs.value <= 0) return null
  const totalMinutes = Math.floor(durationMs.value / 60000)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours === 0) return `${minutes} minutes`
  if (minutes === 0) return `${hours} hour${hours !== 1 ? 's' : ''}`
  return `${hours} hour${hours !== 1 ? 's' : ''} ${minutes} minutes`
})

const rangeExceedsMax = computed(() => {
  if (durationMs.value === null) return false
  return durationMs.value > MAX_BOUNTY_WINDOW_DAYS * 86400000
})

function validate(): boolean {
  errors.value = []
  const now = Date.now()
  const start = form.value.windowStartDateTime
  const end = form.value.windowEndDateTime

  if (!start || !end) {
    errors.value.push('Both a start and end date are required.')
    return false
  }
  if (start.getTime() >= now) {
    errors.value.push('The start date must be in the past.')
    return false
  }
  if (end.getTime() >= now) {
    errors.value.push('The end date must be in the past.')
    return false
  }
  if (end.getTime() <= start.getTime()) {
    errors.value.push('The end date must be after the start date.')
    return false
  }
  if (end.getTime() - start.getTime() > MAX_BOUNTY_WINDOW_DAYS * 86400000) {
    errors.value.push(`The time window cannot exceed ${MAX_BOUNTY_WINDOW_DAYS} days.`)
    return false
  }
  return true
}

defineExpose({ validate })
</script>

<template>
  <div>
    <h6 class="text-h6 q-mt-none q-mb-xs text-blue-grey-9">When did it happen?</h6>
    <p class="text-body2 text-blue-grey-8 q-mb-md">
      Specify when the incident you're looking for occurred. You'll be notified of any footage
      uploaded within this time window and location.
    </p>

    <div class="row q-col-gutter-md q-mb-md">
      <div class="col-12 col-md-6">
        <div class="text-subtitle2 text-blue-grey-8 q-mb-sm">Window Start</div>
        <date-time-picker
          v-model="startDate"
          filled
          label="Start date and time"
          class="full-width"
        />
      </div>
      <div class="col-12 col-md-6">
        <div class="text-subtitle2 text-blue-grey-8 q-mb-sm">Window End</div>
        <date-time-picker v-model="endDate" filled label="End date and time" class="full-width" />
      </div>
    </div>

    <div v-if="form.windowTimezone" class="text-caption text-blue-grey-6 q-mb-sm">
      <q-icon name="mdi-clock-outline" class="q-mr-xs" />
      Times are relative to {{ form.windowTimezone }}
    </div>

    <div v-if="durationLabel && !rangeExceedsMax" class="text-caption text-blue-grey-7 q-mb-sm">
      <q-icon name="mdi-timer-outline" class="q-mr-xs" />
      Duration: {{ durationLabel }}
    </div>

    <q-banner v-if="rangeExceedsMax" rounded class="bg-orange-1 text-orange-9 q-mb-sm">
      <template #avatar>
        <q-icon name="mdi-alert" color="orange-7" />
      </template>
      The selected window exceeds {{ MAX_BOUNTY_WINDOW_DAYS }} days. Please narrow the range.
    </q-banner>

    <q-banner v-for="err in errors" :key="err" rounded class="bg-red-1 text-red-9 q-mb-xs">
      <template #avatar>
        <q-icon name="mdi-alert-circle" color="red-7" />
      </template>
      {{ err }}
    </q-banner>

    <div class="text-caption text-blue-grey-5 q-mt-sm">
      The time window can be up to {{ MAX_BOUNTY_WINDOW_DAYS }} days long.
    </div>
  </div>
</template>
